import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';

import { ShoppingCartService } from '../../../shared/services/apis/platform/shopping-cart.service';
import { GetShoppingCartResponse, ShoppingCartItemResponse } from '../../../shared/services/responses/platform/get-shopping-cart.response';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { ShoppingCartItemComponent } from './shopping-cart-item/shopping-cart-item.component';
import { UtilService } from '../../../shared/services/util.service';
import { KitDetailsService } from '../../../shared/services/apis/platform/kit-details.service';
import { CorreiosEstimate } from '../../../shared/services/responses/platform/kit-details.response';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AuthService } from '../../../shared/services/apis/platform/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginComponent } from '../../../shared/components/login/login.component';

@Component({
  selector: 'app-shopping-cart',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    MenuComponent,
    FooterComponent,
    EmptyListComponent,
    ShoppingCartItemComponent,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    NgxMaskDirective
  ],
  providers: [provideNgxMask()],
  templateUrl: './shopping-cart.component.html',
  styleUrls: ['./shopping-cart.component.css']
})
export class ShoppingCartComponent implements OnInit, OnDestroy {
  public cart: GetShoppingCartResponse | null = null;
  public isLoading = true;
  public isUpdating = false;
  public cep = '';
  public selectedShipping: string | null = null;

  @ViewChild('appMenu') menuComponent!: MenuComponent;


  private destroy$ = new Subject<void>();

  // Debounce das alterações de quantidade: acumula o último valor por kit e só envia 1s após o último clique
  private pendingQuantities = new Map<number, number>();
  private quantityFlush$ = new Subject<void>();
  private flushInProgress = false;
  private afterFlushAction: (() => void) | null = null;

  constructor(
    private shoppingCartService: ShoppingCartService,
    private kitDetailsService: KitDetailsService,
    private authService: AuthService,
    private alertService: AlertService,
    private router: Router,
    public dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.loadCart();

    this.quantityFlush$
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(() => this.flushPendingQuantities());
  }

  ngOnDestroy(): void {
    // Best effort: envia alterações ainda pendentes antes de sair da tela
    this.pendingQuantities.forEach((quantity, idKit) =>
      this.shoppingCartService.updateQuantity(idKit, quantity).subscribe());
    this.pendingQuantities.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCart(): void {
    this.isLoading = true;
    this.shoppingCartService.getShoppingCart(this.cep)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.cart = response;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.alertService.show('Erro', 'Não foi possível carregar o carrinho.', AlertType.error);
        }
      });
  }

  handleQuantityChanged(item: { idKit: number; quantity: number }): void {
    if (item.quantity <= 0) {
      // Remoção: imediata (já confirmada por dialog) e cancela pendência do kit
      this.pendingQuantities.delete(item.idKit);
      this.removeItem(item.idKit);
      return;
    }

    const cartItem = this.cart?.listShoppingCart.find(i => i.idKit === item.idKit);
    if (cartItem)
      cartItem.quantity = item.quantity; // UI otimista; o request sai 1s após o último clique
    this.recalculateTotals();

    this.pendingQuantities.set(item.idKit, item.quantity);
    this.isUpdating = true;
    this.quantityFlush$.next();
  }

  private removeItem(idKit: number): void {
    this.isUpdating = true;
    this.shoppingCartService.updateQuantity(idKit, 0)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.isError) {
            this.isUpdating = false;
            this.alertService.show('Erro', response.errorDescription || 'Não foi possível remover o item.', AlertType.error);
            this.loadCart();
            return;
          }
          this.cart!.listShoppingCart = this.cart!.listShoppingCart.filter(i => i.idKit !== idKit);
          this.recalculateTotals();
          this.menuComponent.verifyCart();
          this.isUpdating = this.pendingQuantities.size > 0 || this.flushInProgress;
        },
        error: () => {
          this.isUpdating = false;
          this.alertService.show('Erro', 'Não foi possível remover o item.', AlertType.error);
          this.loadCart();
        }
      });
  }

  private flushPendingQuantities(): void {
    if (this.flushInProgress)
      return; // o batch em voo reflusha ao terminar

    if (this.pendingQuantities.size === 0) {
      this.isUpdating = false;
      const action = this.afterFlushAction;
      this.afterFlushAction = null;
      action?.();
      return;
    }

    this.flushInProgress = true;
    this.isUpdating = true;
    const updates = Array.from(this.pendingQuantities.entries());
    this.pendingQuantities.clear();

    forkJoin(updates.map(([idKit, quantity]) => this.shoppingCartService.updateQuantity(idKit, quantity)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => {
          this.flushInProgress = false;
          if (responses.some(r => r.isError)) {
            this.afterFlushAction = null;
            this.isUpdating = false;
            this.alertService.show('Erro', 'Não foi possível atualizar o carrinho.', AlertType.error);
            this.loadCart();
            return;
          }
          this.menuComponent.verifyCart();
          this.flushPendingQuantities(); // reflusha cliques que chegaram durante o batch; senão encerra e executa ação agendada
        },
        error: () => {
          this.flushInProgress = false;
          this.afterFlushAction = null;
          this.isUpdating = false;
          this.alertService.show('Erro', 'Não foi possível atualizar o item.', AlertType.error);
          this.loadCart();
        }
      });
  }

  recalculateTotals(): void {
    if (!this.cart) return;

    this.cart.sunTotalPriceKits = this.cart.listShoppingCart.reduce((acc, item) => {
      return acc + (item.totalPrice * item.quantity);
    }, 0);
  }

  calculateShipping(): void {
    if (!this.cep || this.cep.replace(/\D/g, '').length !== 8) {
      this.alertService.show('Atenção', 'Por favor, insira um CEP válido.', AlertType.warning);
      return;
    }

    this.loadCart();
  }

  get subtotal(): number {
    if (!this.cart || !this.cart.listShoppingCart) {
      return 0;
    }
    return this.cart.listShoppingCart.reduce((acc, item) => acc + (item.totalPrice * item.quantity), 0);
  }

  get selectedShippingCost(): number {
    if (!this.selectedShipping || this.cart?.listCorreios?.length === 0) {
      return 0;
    }
    const selectedOption = this.cart?.listCorreios?.find(option => option.serviceCode === this.selectedShipping);
    return selectedOption ? selectedOption.price : 0;
  }

  get grandTotal(): number {
    return this.subtotal + this.selectedShippingCost;
  }

  goToCheckout(): void {
    if (!this.authService.isAuthenticated()) {
      this.openModalLogin();
      return;
    }

    if (this.pendingQuantities.size === 0 && !this.flushInProgress) {
      this.router.navigate(['/comprar']);
      return;
    }

    // Fura o debounce: envia as pendências agora e navega quando concluir
    this.afterFlushAction = () => this.router.navigate(['/comprar']);
    this.flushPendingQuantities();
  }

  continueShopping(): void {
    this.router.navigate(['/']);
  }

  openModalLogin() {
    this.dialog.open(LoginComponent, {
      data: {},
    });
  }
}